import React, { useEffect, useMemo, useState } from "react";
import {
    Typography,
    Box,
    CircularProgress,
    Button,
    debounce,
} from "@mui/material";
import axios from "axios";
import CustomTable from "../components/CustomTable";
import { useNavigate, useParams } from "react-router-dom";
import to from "await-to-js";
import Grid from "@mui/material/Grid2";
import { axiosWithAuth } from "../plugins/axios";
import {
    DataGrid,
    GridColDef,
    GridColumnMenuContainer,
    GridColumnMenuProps,
    GridColumnMenuSortItem,
    GridColumnMenuHideItem,
    GridColumnMenuManageItem,
    GridToolbarContainer,
    useGridApiContext,
    GridSortModel,
    GridCellParams,
    getGridStringOperators,
    GridFilterModel,
    GridRenderCellParams,
} from "@mui/x-data-grid";
import { downloadExcelForDataGrid } from "../lib/utils/common";

function CustomColumnMenu(props: GridColumnMenuProps) {
    const { hideMenu, colDef, open } = props;
    return (
        <GridColumnMenuContainer hideMenu={hideMenu} colDef={colDef} open={open}>
            <GridColumnMenuSortItem colDef={colDef} onClick={hideMenu} />
            <GridColumnMenuHideItem colDef={colDef} onClick={hideMenu} />
            <GridColumnMenuManageItem colDef={colDef} onClick={hideMenu} />
        </GridColumnMenuContainer>
    );
}

function CustomNoRowsOverlay({ message }: { message: string }) {
    const isError = message.startsWith("Error");
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
            }}
        >
            <Typography color={isError ? "error" : "#000"}>{message}</Typography>
        </Box>
    );
}

function EvaluationDetails() {
    const [evaluationDetails, setEvaluationDetails] = useState<any>([]);
    const [errMessage, setErrorMessage] = useState<any>(null);
    const [cohortDetails, setCohortDetails] = useState<any>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [submissionId, setSubmissionId] = useState<string | null>(null);
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [answerFile, setAnswerFile] = useState<string | null>(null);

    const { cohortId, quizId, evaluationId } = useParams();
    const apiUrl = process.env.REACT_APP_PIPELINE_API;
    const navigate = useNavigate();

    const debouncedFetchCourseDetails = useMemo(
        () =>
            debounce((filter) => {
                fetchCourseDetails(filter);
            }, 1000),
        [sortModel]
    );

    const handleFilterChange = (filterModel: GridFilterModel) => {
        const filter = {
            filterColumn: filterModel.items[0]?.field,
            filterValue: filterModel.items[0]?.value,
            filterOperator: filterModel.items[0]?.operator,
        };
        debouncedFetchCourseDetails(filter);
    };
    const fetchCourseDetails = async (filter?: any) => {
        const [err, response] = await to(
            axiosWithAuth(
                `${apiUrl}cohortConfiguration/cohort/${cohortId}/quiz/${quizId}`,
                {
                    params: {
                        sortColumn: sortModel[0]?.field || null,
                        sortDirection: sortModel[0]?.sort || null,
                        ...filter,
                    },
                },
                "get"
            )
        );

        if (err) {
            setError("Failed to load course details");
            setLoading(false);
            return;
        }

        setCohortDetails(response!.data);
    };

    useEffect(() => {
        return () => {
            debouncedFetchCourseDetails.clear();
        };
    }, [debouncedFetchCourseDetails]);

    const flattenData = async (data: any) => {
        const result = [] as any;

        data?.forEach((l1: any) => {
            const l1Name = l1.name;
            const comment = l1.comment;

            if (!l1.children || l1.children.length === 0) {
                result.push({
                    ...l1,
                    l1Name,
                    comment,
                    l2Name: "-",
                    l3Name: "-",
                });
            } else {
                l1.children.forEach((l2: any) => {
                    const l2Name = l2.name;

                    if (!l2.children || l2.children.length === 0) {

                        result.push({
                            ...l2,
                            l2Name,
                            l1Name,
                            comment,
                            l3Name: "-",
                        });
                    } else {
                        l2.children.forEach((l3: any) => {
                            result.push({
                                ...l3,
                                l3Name: l3.name,
                                l2Name,
                                l1Name,
                                comment,
                            });
                        });
                    }
                });
            }
        });

        return result;
    }

    // const flattenData = async (data: any) => {
    //   const result = [] as any;

    //   data?.forEach((l1: any) => {
    //     const l1Name = l1.name;
    //     const comment = l1.comment;

    //     l1.children?.forEach((l2: any) => {
    //       const l2Name = l2.name;

    //       l2.children?.forEach((l3: any) => {
    //         result.push({
    //           ...l3,
    //           l3Name: l3.name,
    //           l2Name,
    //           l1Name,
    //           comment,
    //         });
    //       });
    //     });
    //   });
    //   return result;
    // };

    // function DownloadButton() {
    //     return (
    //         <>
    //             <Button
    //                 onClick={() => {
    //                     downloadExcelForDataGrid({
    //                         rows: evaluationDetails,
    //                         columns: columns,
    //                         fileName: `cohort-${cohortId}-quiz-${quizId}-evaluation-${evaluationId}.xlsx`,
    //                     });
    //                 }}
    //                 variant="outlined"
    //                 color="primary"
    //                 sx={{ marginLeft: "auto", marginBottom: "2rem", display: "flex" }}
    //             >
    //                 Download report
    //             </Button>
    //         </>
    //     );
    // }
    function DownloadButton() {
        return (
            <Box
                display="flex"
                justifyContent="flex-end"
                sx={{
                    marginTop: '2rem',
                    marginBottom: '2rem',
                    borderRadius: 2,
                    padding: 3
                }}
            >
                <Button
                    onClick={() => {
                        downloadExcelForDataGrid({
                            rows: evaluationDetails,
                            columns: columns,
                            fileName: `cohort-${cohortId}-quiz-${quizId}-evaluation-${evaluationId}.xlsx`,
                        });
                    }}
                    variant="outlined"
                    color="primary"
                    sx={{
                        marginRight: "1rem",
                    }}
                >
                    Download report
                </Button>

                {answerFile && (
                    <Button
                        onClick={() => {
                            window.open(answerFile, '_blank');
                        }}
                        variant="outlined"
                        color="primary"

                    >
                        Download Submission File
                    </Button>
                )}
            </Box>
        );
    }


    useEffect(() => {
        const fetchEvaluationDetails = async () => {
            const [err, response] = await to(
                axiosWithAuth(
                    `${apiUrl}autograde/evaluations/${cohortId}/${quizId}/${evaluationId}`,
                    {},
                    "get"
                )
            );
            console.log({ response });

            if (err) {
                setError("Failed to load evaluation details");
                setLoading(false);
                return;
            }

            setSubmissionId(response.data.submissionId);
            console.log(response.data.submissionId);
            setAnswerFile(response.data?.webhookId?.answer_file);


            const {
                humanEvaluatedRubrics: humanRubrics = {},
                aiEvaluatedRubrics: aiEvaluatedRubricsData = {},
                failureReason,
            } = response.data;

            const humanEvaluatedRubrics = humanRubrics?.children ?? humanRubrics?.rubric?.children ?? [];

            const { children: aiEvaluatedRubrics = [] } =
                aiEvaluatedRubricsData || {};

            let evaluationData;

            if (humanEvaluatedRubrics.length > 0) {
                const [humanErr, humanFlattedData] = await to(
                    flattenData(humanEvaluatedRubrics)
                );
                const [aiErr, l3Data] = await to(flattenData(aiEvaluatedRubrics));

                if (humanErr || aiErr) {
                    setError("Failed to process evaluation data");
                    setLoading(false);
                    return;
                }

                evaluationData = l3Data.map((l3: any) => {
                    const humanFeedback = humanFlattedData.find(
                        (h: any) => h.id === l3.id
                    );
                    const { feedback: { feedback: { feedback: { feedback } } }, feedback: humanFeedbackDetails } = humanFeedback
                    return {
                        ...l3,
                        humanScore: humanFeedback ? humanFeedback.score : 0,
                        humanFeedback: humanFeedback ? humanFeedback.comment || feedback || humanFeedbackDetails : "",
                    };
                });
            } else {
                const [aiErr, l3Data] = await to(flattenData(aiEvaluatedRubrics));
                if (aiErr) {
                    setError("Failed to process evaluation data");
                    setLoading(false);
                    return;
                }
                evaluationData = l3Data;
            }

            evaluationData = evaluationData.map((item: any) => ({
                ...item,
                gradingGuideline: item.gradingGuidelines || item.gradingGuideline || "-",
            }));

            setEvaluationDetails(evaluationData);
            setLoading(false);

            setErrorMessage(failureReason);
        };

        fetchEvaluationDetails();
        fetchCourseDetails();
    }, [apiUrl, page, rowsPerPage, sortModel]);

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return (
            <Box mt={4} display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box mt={4}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }
    const renderStyledCell = (params: GridCellParams) => (
        <div
            style={{
                maxHeight: "70px",
                overflowY: "auto",
                lineHeight: "1.1rem",
                whiteSpace: "normal",
                wordWrap: "break-word",
                display: "block",
                WebkitLineClamp: 5,
                WebkitBoxOrient: "vertical",
                textAlign: "center",
                justifyContent: "center",
            }}
            dangerouslySetInnerHTML={{ __html: params.value as string }}
        ></div>
    );

    const getCustomFilterOperators = () =>
        getGridStringOperators().filter(
            (operator) => operator.value === "contains" || operator.value === "equals"
        );


    const calculateDeviation = (aiScore: number | null, humanScore: number | null) => {
        if (aiScore === 0 && humanScore === 0) {
            return '0%';
        } else if (aiScore === null || humanScore === null) {
            return '-';
        } else if (aiScore === 0) {
            return '-100%';
        } else if (humanScore === 0) {
            return '100%';
        } else {
            const deviation = ((aiScore - humanScore) * 100) / aiScore;
            return `${deviation.toFixed(2)}%`;
        }
    }

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "Rubric ID",
            width: 100,
            renderHeader: (params) => (
                <span className="whitespace-normal">{params.colDef.headerName}</span>
            ),
            filterOperators: getCustomFilterOperators(),
        },
        {
            field: "l1Name",
            headerName: "L1",
            flex: 1,
            renderHeader: (params) => (
                <span className="whitespace-normal">{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <span className="whitespace-normal">
                    {params.value ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: params.value,
                            }}
                        />
                    ) : (
                        "-"
                    )}
                </span>
            ),
            filterOperators: getCustomFilterOperators(),
        },
        {
            field: "l2Name",
            headerName: "L2",
            flex: 1,
            renderHeader: (params) => (
                <span className="whitespace-normal">{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <span className="whitespace-normal">
                    {params.value ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: params.value,
                            }}
                        />
                    ) : (
                        "-"
                    )}
                </span>
            ),
            filterOperators: getCustomFilterOperators(),
        },
        {
            field: "l3Name",
            headerName: "L3",
            flex: 1,
            renderHeader: (params) => (
                <span className="whitespace-normal">{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <span className="whitespace-normal">
                    {params.value ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: params.value,
                            }}
                        />
                    ) : (
                        "-"
                    )}
                </span>
            ),
            filterOperators: getCustomFilterOperators(),
        },
        {
            field: "gradingGuideline",
            headerName: "Grading Guidelines",
            flex: 2,
            renderHeader: (params) => (
                <span className="whitespace-normal">{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {

                // const value = params.row?.gradingGuidelines || params.row?.gradingGuideline;

                return (
                    <span className="whitespace-normal">
                        {params.value ? (
                            <div dangerouslySetInnerHTML={{ __html: params.value }} />
                        ) : (
                            "-"
                        )}
                    </span>
                );
            },
            filterOperators: getCustomFilterOperators(),
        },
        {
            field: "maxMarks",
            headerName: "Total Marks",
            flex: 1,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        whiteSpace: "normal",
                        alignItems: "center",
                    }}
                >
                    {params.colDef.headerName}
                </Box>
            ),
            filterOperators: getCustomFilterOperators(),

        },
        {
            field: "humanScore",
            headerName: "Manual Grader Marks",
            flex: 1,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        whiteSpace: "normal",
                        alignItems: "center",
                    }}
                >
                    {params.colDef.headerName}
                </Box>
            ),
            filterOperators: getCustomFilterOperators(),
        },
        {
            field: "score",
            headerName: "Autograder Marks",
            flex: 1,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        whiteSpace: "normal",
                        alignItems: "center",
                    }}
                >
                    {params.colDef.headerName}
                </Box>
            ),
            filterOperators: getCustomFilterOperators(),
        },
        {
            field: "deviation",
            headerName: "Deviation",
            flex: 1,
            renderHeader: (params) => (
                <span className="whitespace-normal">{params.colDef.headerName}</span>
            ),
            filterOperators: getCustomFilterOperators(),
            renderCell: (params: GridRenderCellParams) => {

                const humanScore = params.row?.humanScore ?? 0;
                const aiScore = params.row?.score ?? 0;

                if (humanScore === 0 && aiScore === 0) {
                    return "0%";
                }
                let val: string | undefined = calculateDeviation(aiScore, humanScore);

                if (val) {
                    val = val.slice(0, -1);
                    val = `${Math.round(Number(val))}%`;
                }
                return val || "-";
            },
        },

        {
            field: "humanFeedback",
            headerName: "Manual Grader Comments",
            flex: 2,
            renderHeader: (params) => (
                <span className="whitespace-normal">{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <Box className="whitespace-normal flex justify-center">
                    {params.value ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: params.value,
                            }}
                        />
                    ) : (
                        "-"
                    )}
                </Box>
            ),
            filterOperators: getCustomFilterOperators(),
            cellClassName: "manual-grader-comments-cell",
        },
        {
            field: "feedback",
            headerName: "Autograder Comments",
            flex: 2,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        whiteSpace: "normal",
                    }}
                >
                    {params.colDef.headerName}
                </Box>
            ),
            renderCell: (params) => (
                <span className="whitespace-normal">
                    {params.value ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: params.value,
                            }}
                        />
                    ) : (
                        "-"
                    )}
                </span>
            ),
            filterOperators: getCustomFilterOperators(),
        },
        {
            field: "evidence",
            headerName: "Autograder Evidence",
            flex: 2,
            renderHeader: (params) => (
                <span className="whitespace-normal">{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <span className="whitespace-normal">
                    {params.value ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: params.value,
                            }}
                        />
                    ) : (
                        "-"
                    )}
                </span>
            ),
            filterOperators: getCustomFilterOperators(),
        },
    ];

    return (
        <Box mt={4}>
            <Box pb={4}>
                <Grid container spacing={2}>
                    <Grid size={4}>
                        <Typography gutterBottom>
                            Cohort:
                            <Typography display={"inline"} fontWeight={"bold"}>
                                {cohortId}
                            </Typography>
                        </Typography>
                        <Typography fontWeight={"bold"}>
                            {cohortDetails.cohortName}
                        </Typography>
                    </Grid>
                    <Grid size={4}>
                        <Typography gutterBottom>
                            Quiz:
                            <Typography display={"inline"} fontWeight={"bold"}>
                                {quizId}
                            </Typography>
                        </Typography>
                        <Typography gutterBottom fontWeight={"bold"}>
                            {cohortDetails.quizName}
                        </Typography>
                    </Grid>
                    <Grid size={4}>
                        <Typography gutterBottom>
                            Evaluation ID:
                            <Typography display={"inline"} fontWeight={"bold"}>
                                {submissionId}
                            </Typography>
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
            <Typography variant="h4" gutterBottom>
                Evaluation Details
            </Typography>
            {/* <Box justifyContent={"right"} display={"flex"}>
                <Button>Run / Re Run Evaluations</Button>
            </Box> */}
            <Box>
                <DownloadButton />
                <DataGrid
                    rows={evaluationDetails}
                    columns={columns.map((column) => ({
                        ...column,
                        align: "left",
                        headerAlign: "center",
                    }))}
                    autoHeight
                    hideFooter
                    getRowId={(row) => row.id}
                    slots={{
                        // columnMenu: CustomColumnMenu,
                        noRowsOverlay: () => (
                            <CustomNoRowsOverlay
                                message={
                                    errMessage ? `Error: ${errMessage}` : "No data available"
                                }
                            />
                        ),
                    }}
                    onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                    getRowHeight={() => "auto"}
                    sx={{
                        mb: 3,
                        "& .MuiDataGrid-cell": {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "!flex-start",
                            whiteSpace: "normal",
                            paddingTop: "10px",
                            paddingBottom: "10px",
                        },
                        "& .MuiDataGrid-columnHeader": {
                            fontSize: "14px !important",
                            fontWeight: "800 !important",
                        },
                    }}
                    onFilterModelChange={handleFilterChange}
                />
            </Box>
        </Box>
    );
}
export default EvaluationDetails;
